import { 
  collection, doc, getDoc, getDocs, addDoc, setDoc, 
  updateDoc, query, orderBy, serverTimestamp,
  onSnapshot, where, deleteDoc
} from 'firebase/firestore';
import {
  get_time,
  scroll_to_bottom,
  get_messages,
  get_date_from_now,
  is_date_change,
  send_message,
  set_typing,
  is_image,
  get_avatar_html,
} from './chat_utils';

export default class ChatSpace {
  constructor(opts) {
    this.chat_list = opts.chat_list;
    this.$wrapper = opts.$wrapper;
    this.profile = opts.profile;
    this.file = null;
    this.$chat_space = $(document.createElement('div')).addClass('chat-space');
    this.$chat_space_container = $(document.createElement('div')).addClass('chat-space-container');
    this.$chat_space.append(this.$chat_space_container);
    this.setup();
  }

  setup() {
    this.typing = false;
    this.setup_header();
    this.fetch_and_setup_messages();
    this.setup_socketio();
  }

  setup_header() {
    this.avatar_html = get_avatar_html(
      this.profile.room_type,
      this.profile.opposite_person_email,
      this.profile.room_name
    );
    const header_html = `
      <div class='chat-header'>
        ${
          this.profile.is_admin === true
            ? `<span class='chat-back-button' title='${__('Go Back')}' >
                ${frappe.utils.icon('left')}
              </span>`
            : ``
        }
        ${this.avatar_html}
        <div class='chat-profile-info'>
          <div class='chat-profile-name'>
          ${__(this.profile.room_name)}
          <div class='online-circle'></div>
          </div>
          <div class='chat-profile-status'>${__('Typing...')}</div>
        </div>
      </div>
    `;
    this.$chat_space.prepend(header_html);
  }

  async fetch_and_setup_messages() {
    try {
      const res = await get_messages(
        this.profile.room,
        this.profile.user_email
      );
      this.setup_messages(res);
      this.setup_actions();
      this.render();
    } catch (error) {
      console.error('Error fetching messages:', error);
      frappe.msgprint({
        title: __('Error'),
        message: __('Something went wrong. Please refresh and try again.'),
      });
    }
  }

  setup_messages(messages_list) {
    this.prevMessage = {};
    this.message_html = ``;
    
    if (!messages_list) return;

    if (this.profile.message) {
      messages_list.push(this.profile.message);
      send_message(
        this.profile.message.content,
        this.profile.user,
        this.profile.room,
        this.profile.user_email
      );
    }

    messages_list.forEach((element) => {
      if (!element) return;
      
      const date_line_html = this.make_date_line_html(element.creation);
      this.message_html += date_line_html;

      let message_type = 'sender';

      if (element.sender_email === this.profile.user_email) {
        message_type = 'recipient';
      } else if (this.profile.room_type === 'Guest') {
        if (this.profile.is_admin === true && element.sender !== 'Guest') {
          message_type = 'recipient';
        }
      }

      const messageElement = this.make_message(
        element.content,
        get_time(element.creation),
        message_type,
        element.sender
      );

      if (messageElement) {
        this.message_html += messageElement.prop('outerHTML');
      }

      this.prevMessage = element;
    });

    this.$chat_space_container.html(this.message_html);
  }

  make_date_line_html(dateObj) {
    let result = `
			<div class='date-line'>
				<span>
					${__(get_date_from_now(dateObj, 'space'))}
				</span>
			</div>
		`;
    if ($.isEmptyObject(this.prevMessage)) {
      return result;
    } else if (is_date_change(dateObj, this.prevMessage.creation)) {
      return result;
    } else {
      return '';
    }
  }

  setup_actions() {
    this.$chat_actions = $(document.createElement('div'));
    this.$chat_actions.addClass('chat-space-actions');
    const chat_actions_html = `
      <div class="image-preview-container" style="display: none;">
        <div class="image-preview">
          <div class="preview-close">×</div>
          <img src="" alt="Preview" style="max-width: 200px; max-height: 200px;">
        </div>
      </div>
			<span class='open-attach-items'>
				${frappe.utils.icon('attachment', 'lg')}
			</span>
			<input type='file' id='chat-file-uploader' 
				accept='image/*, application/pdf, .doc, .docx'
				style='display: none;'
			>
			<input class='form-control type-message' 
				type='search' 
				placeholder='${__('Type message or paste image (Ctrl+V)')}'
			>
			<div>
				<span class='message-send-button'>
					<svg xmlns="http://www.w3.org/2000/svg" width="1.1rem" height="1.1rem" viewBox="0 0 24 24">
						<path d="M24 0l-6 22-8.129-7.239 7.802-8.234-10.458 7.227-7.215-1.754 24-12zm-15 16.668v7.332l3.258-4.431-3.258-2.901z"/>
					</svg>
				</span>
			</div>
		`;
    this.$chat_actions.html(chat_actions_html);
    this.$chat_space.append(this.$chat_actions);

    // Add styles for image preview
    const style = document.createElement('style');
    style.textContent = `
      .chat-space-actions {
        position: relative;
      }
      .image-preview-container {
        position: absolute;
        bottom: calc(100% + 5px);
        left: 0;
        right: 0;
        padding: 8px;
        background: var(--card-bg);
        border: 1px solid var(--border-color);
        border-radius: 6px;
        margin: 0 10px;
      }
      .image-preview {
        position: relative;
        display: flex;
        justify-content: center;
      }
      .preview-close {
        position: absolute;
        top: -8px;
        right: -8px;
        width: 20px;
        height: 20px;
        background: var(--gray-600);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 16px;
        line-height: 1;
      }
      .preview-close:hover {
        background: var(--gray-800);
      }
    `;
    document.head.appendChild(style);
  }

  async handle_upload_file() {
    try {
      const file = this.file.file_obj;
      const isImageOrAudio = file.type.startsWith('image/') || file.type.startsWith('audio/');

      if (isImageOrAudio) {
        // Convert file to base64
        const base64Data = await this.fileToBase64(file);
        
        // Generate a unique code for the file
        const imageCode = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9]/g, '_')}`;
        
        // Call the digital asset API
        const response = await fetch('https://apim.digitalbiz.com.vn:8243/digitalasset/insertupdate/v1', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            data: [{
              branchCode: "CX",
              tenantCode: "cx_dbiz",
              imageCode: "",
              image64: base64Data
            }]
          })
        });

        if (!response.ok) {
          throw new Error('Failed to upload to digital asset API');
        }

        const result = await response.json();
        
        // Create message with the file URL from the API response
        await this.handle_send_message(result.url || result.data[0].url);

        frappe.show_alert({
          message: __('File uploaded successfully'),
          indicator: 'green'
        });
      } else {
        // Handle other file types with regular upload
        const form_data = new FormData();
        form_data.append('file', file, file.name);
        form_data.append('is_private', 0);

        const response = await fetch('/api/method/upload_file', {
          method: 'POST',
          headers: {
            'X-Frappe-CSRF-Token': frappe.csrf_token
          },
          body: form_data
        });

        if (!response.ok) {
          throw new Error('Failed to upload file');
        }

        const result = await response.json();
        
        if (result.message) {
          await this.handle_send_message(result.message.file_url);

          frappe.show_alert({
            message: __('File uploaded successfully'),
            indicator: 'green'
          });
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      frappe.show_alert({
        message: __('Error uploading file'),
        indicator: 'red'
      });
    } finally {
      this.file = null;
    }
  }

  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:image/xyz;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  get_message_type(fileType) {
    if (fileType.startsWith('image/')) return 'image';
    if (fileType.startsWith('audio/')) return 'audio';
    if (fileType.startsWith('video/')) return 'video';
    return 'file';
  }

  setup_events() {
    const me = this;

    //Timeout function
    me.typing_timeout = () => {
      me.typing = false;
    };

    $('.chat-back-button').on('click', function () {
      me.chat_list.render_messages();
      me.chat_list.render();
    });

    $('.open-attach-items').on('click', function () {
      $('#chat-file-uploader').click();
    });

    $('#chat-file-uploader').on('change', function () {
      if (this.files.length > 0) {
        me.file = {};
        me.file.file_obj = this.files[0];
        me.handle_upload_file(me.file);
        me.file = null;
      }
    });

    // Handle paste event
    $('.type-message').on('paste', async function(e) {
      const items = (e.clipboardData || e.originalEvent.clipboardData).items;
      
      try {
        let hasImage = false;
        for (const item of items) {
          if (item.type.indexOf('image') === 0) {
            e.preventDefault();
            hasImage = true;
            const file = item.getAsFile();
            
            if (!file) {
              frappe.show_alert({
                message: __('Failed to read image from clipboard'),
                indicator: 'red'
              });
              return;
            }

            // Show image preview
            const reader = new FileReader();
            reader.onload = function(e) {
              $('.image-preview img').attr('src', e.target.result);
              $('.image-preview-container').show();
              me.pastedImage = file;
            };
            reader.onerror = function() {
              frappe.show_alert({
                message: __('Failed to preview image'),
                indicator: 'red'
              });
            };
            reader.readAsDataURL(file);
            break;
          }
        }
        
        if (!hasImage && items.length > 0) {
          frappe.show_alert({
            message: __('No image found in clipboard'),
            indicator: 'yellow'
          });
        }
      } catch (error) {
        console.error('Error handling paste:', error);
        frappe.show_alert({
          message: __('Error handling pasted content'),
          indicator: 'red'
        });
      }
    });

    // Handle preview actions
    $('.preview-close').on('click', function() {
      me.pastedImage = null;
      $('.image-preview-container').hide();
    });

    $('.message-send-button').on('click', function () {
      if (me.pastedImage) {
        me.file = {
          file_obj: me.pastedImage
        };
        me.handle_upload_file(me.file);
        me.file = null;
        me.pastedImage = null;
        $('.image-preview-container').hide();
      } else {
        me.handle_send_message();
      }
    });

    $('.type-message').keydown(function (e) {
      if (e.which === 13 && !e.shiftKey) {
        me.handle_send_message();
      } else {
        //Set as typing
        if (me.typing === false) {
          me.typing = true;
          set_typing(
            me.profile.room,
            me.profile.user_email,
            me.typing,
            !me.profile.is_admin
          );
          me.timeout = setTimeout(me.typing_timeout, 3000);
        }
      }
    });
  }

  setup_socketio() {
    const me = this;
    
    // Set up Firestore listener for messages
    const messagesRef = collection(frappe.firebase.db, 'messages', this.profile.room, 'messageList');
    const q = query(messagesRef, orderBy('creation', 'asc'));
    
    // Create message listener
    this.messageListener = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const message = change.doc.data();
          // Only process messages that were sent after we started listening
          const messageTime = new Date(message.creation);
          if (messageTime > me.lastFetchTime) {
            me.receive_message({
              content: message.content,
              user: message.user,
              creation: message.creation,
              sender_email: message.sender_email
            }, message.creation);
          }
        }
      });
    });
    
    // Set up typing indicator listener
    const roomRef = doc(frappe.firebase.db, 'rooms', this.profile.room);
    this.typingListener = onSnapshot(roomRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.typing) {
          // Process typing status updates
          me.get_typing_changes(data.typing);
        } else {
          // No one is typing
          $('.chat-profile-status').css('visibility', 'hidden');
        }
      }
    });
    
    // Store timestamp to avoid processing old messages
    this.lastFetchTime = new Date();
  }

  // Update typing status
  update_typing(is_typing) {
    try {
      // Update typing status in Firestore
      const roomRef = doc(frappe.firebase.db, 'rooms', this.profile.room);
      
      // First get the current room data
      getDoc(roomRef).then((docSnap) => {
        if (docSnap.exists()) {
          const roomData = docSnap.data();
          const typing = roomData.typing || {};
          
          if (is_typing) {
            // Add/update this user's typing status
            typing[this.profile.user_email] = new Date().toISOString();
          } else {
            // Remove this user's typing status
            delete typing[this.profile.user_email];
          }
          
          // Update the room with new typing status
          updateDoc(roomRef, { typing: typing });
        }
      });
      
      // Handle UI updates for typing status
      if (is_typing) {
        $('.chat-profile-status').css('visibility', 'visible');
      } else {
        $('.chat-profile-status').css('visibility', 'hidden');
      }
    } catch (error) {
      console.error('Error updating typing status:', error);
    }
  }

  // Cleanup method for component destruction
  destroy() {
    if (this.messageListener) {
      this.messageListener();
    }
    if (this.typingListener) {
      this.typingListener();
    }
    if (this.typing_timeout) {
      clearTimeout(this.typing_timeout);
    }
  }

  get_typing_changes(typingUsers) {
    try {
      if (!typingUsers || Object.keys(typingUsers).length === 0) {
        // No one is typing
        $('.chat-profile-status').css('visibility', 'hidden');
        return;
      }

      // Check if someone other than current user is typing
      const isOtherUserTyping = Object.keys(typingUsers).some(user => {
        // Skip current user
        if (user === this.profile.user_email) return false;
        
        // Check if typing timestamp is recent (within last 5 seconds)
        const typingTime = new Date(typingUsers[user]);
        const fiveSecondsAgo = new Date(Date.now() - 5000);
        return typingTime > fiveSecondsAgo;
      });

      if (isOtherUserTyping) {
        $('.chat-profile-status').css('visibility', 'visible');
        
        // Hide typing indicator after 5 seconds if no updates
        if (this.typing_timeout) clearTimeout(this.typing_timeout);
        this.typing_timeout = setTimeout(() => {
          $('.chat-profile-status').css('visibility', 'hidden');
        }, 5000);
      } else {
        $('.chat-profile-status').css('visibility', 'hidden');
      }
    } catch (error) {
      console.error('Error handling typing changes:', error);
    }
  }

  make_message(content, time, type, name) {
    const message_class =
      type === 'recipient' ? 'recipient-message' : 'sender-message';
    const $recipient_element = $(document.createElement('div')).addClass(
      message_class
    );
    const $message_element = $(document.createElement('div')).addClass(
      'message-bubble'
    );

    const $name_element = $(document.createElement('div'))
      .addClass('message-name')
      .text(name);

    let $sanitized_content;

    // Check if content is from digital asset API
    if (content.includes('assets.digitalbiz.com.vn')) {
      $sanitized_content = $(document.createElement('img'));
      $sanitized_content.attr({ src: content }).addClass('img-responsive chat-image');
      $message_element.css({ padding: '0px', background: 'inherit' });
      $name_element.css({
        color: 'var(--text-muted)',
        'padding-bottom': 'var(--padding-xs)',
      });
    } else {
      // Handle regular Frappe file uploads
      const n = content.lastIndexOf('/');
      const file_name = content.substring(n + 1) || '';

      if (content.startsWith('/files/') && file_name !== '') {
        let $url;
        // if (is_image(file_name)) {
        //   $url = $(document.createElement('img'));
        //   $url.attr({ src: content }).addClass('img-responsive chat-image');
        //   $message_element.css({ padding: '0px', background: 'inherit' });
        //   $name_element.css({
        //     color: 'var(--text-muted)',
        //     'padding-bottom': 'var(--padding-xs)',
        //   });
        // } else {
        $url = $(document.createElement('a'));
        $url.attr({ href: content, target: '_blank' }).text(__(file_name));

        if (type === 'sender') {
          $url.css('color', 'var(--cyan-100)');
        }
        // }
        $sanitized_content = $url;
      } else {
        $sanitized_content = __($('<div>').text(content).html());
      }
    }

    if (type === 'sender' && this.profile.room_type === 'Group') {
      $message_element.append($name_element);
    }
    $message_element.append($sanitized_content);
    $recipient_element.append($message_element);
    $recipient_element.append(`<div class='message-time'>${__(time)}</div>`);

    // Add styles for chat images
    // const style = document.createElement('style');
    // style.textContent = `
    //   .chat-image {
    //     max-width: 250px;
    //     max-height: 200px;
    //     border-radius: 8px;
    //     cursor: pointer;
    //   }
    //   .chat-image:hover {
    //     opacity: 0.9;
    //   }
    // `;
    // document.head.appendChild(style);

    // // Add click handler to open image in new tab
    // $recipient_element.find('.chat-image').on('click', function() {
    //   window.open($(this).attr('src'), '_blank');
    // });

    return $recipient_element;
  }

  handle_send_message(attachment) {
    const $type_message = $('.type-message');
    let content = null;

    if (attachment) {
      content = attachment;
    } else {
      content = $type_message.val();
    }

    if (content.length === 0) {
      return;
    }
    this.typing = false;
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    if (
      this.profile.is_admin === true &&
      frappe.Chat.settings.user.enable_message_tone === 1
    ) {
      frappe.utils.play_sound('chat-message-send');
    }

    this.$chat_space_container.append(
      this.make_message(content, get_time(), 'recipient', this.profile.user)
    );
    $type_message.val('');
    scroll_to_bottom(this.$chat_space_container);
    send_message(
      content,
      this.profile.user,
      this.profile.room,
      this.profile.user_email
    );
  }

  receive_message(res, time) {
    if (!this.$chat_space_container) {
      console.error('Chat space container not initialized');
      return;
    }

    if (!res || !res.content) {
      console.error('Invalid message data received');
      return;
    }

    let chat_type = 'sender';
    if (res.sender_email === this.profile.user_email) {
      return;
    }

    if (
      this.profile.is_admin === true &&
      $('.chat-element').is(':visible') &&
      frappe.Chat.settings.user.enable_message_tone === 1
    ) {
      frappe.utils.play_sound('chat-message-receive');
    }

    if (this.profile.room_type === 'Guest') {
      if (this.profile.is_admin === true && res.user !== 'Guest') {
        chat_type = 'recipient';
      }
    }

    const messageElement = this.make_message(res.content, time, chat_type, res.user);
    if (messageElement) {
      this.$chat_space_container.append(messageElement);
      scroll_to_bottom(this.$chat_space_container);
    }
  }

  render() {
    this.$wrapper.html(this.$chat_space);
    this.setup_events();

    scroll_to_bottom(this.$chat_space_container);
  }
}
